import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";

import * as api from "../api.js";
import {
  PageHeader,
  Loading,
  ErrorBanner,
  Tabs,
  StatCard,
  PromptBody,
  ModeToggle,
  formatDate,
} from "../components.jsx";

// After the tenant/sub-tenant restructure the tenant page is identity
// + sub-tenant routing only. Config, Cost+Usage, LLM Credentials,
// Secrets, and Drafts all live on the sub-tenant detail page now —
// each sub-tenant owns its own properties.
const TABS = [
  { id: "overview", label: "Overview" },
  { id: "sub-tenants", label: "Sub-Tenants" },
  { id: "composed", label: "Composed Prompt" },
];

export default function TenantDetail() {
  const { tid } = useParams();
  const [tab, setTab] = useState("overview");
  const [tenant, setTenant] = useState(null);
  const [subTenants, setSubTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [t, subs] = await Promise.all([api.getTenant(tid), api.listSubTenants(tid)]);
      setTenant(t.data);
      setSubTenants(subs.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [tid]);

  useEffect(() => {
    reload();
  }, [reload]);

  return (
    <>
      <PageHeader
        title={tenant?.name || tid}
        subtitle={tenant ? `${subTenants.length} sub-tenant${subTenants.length === 1 ? "" : "s"}` : ""}
        breadcrumbs={[
          { label: "Tenants", to: "/tenants" },
          { label: tenant?.name || tid },
        ]}
      />

      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      <div className="tab-content">
      <ErrorBanner error={error} />

      {loading ? (
        <Loading />
      ) : !tenant ? (
        <div className="empty-state"><h3>Tenant not found</h3></div>
      ) : (
        <>
          {tab === "overview" && (
            <OverviewTab
              tenant={tenant}
              subTenants={subTenants}
              onSaved={reload}
            />
          )}
          {tab === "sub-tenants" && <SubTenantsTab tid={tid} subTenants={subTenants} />}
          {tab === "composed" && <ComposedTab tid={tid} />}
        </>
      )}
      </div>
    </>
  );
}

// ─── Overview tab ────────────────────────────────────────────────────
//
// Identity-only. Operational settings (LLM model, capabilities, brand
// voice, default language) live on the Config tab — they're versioned
// via tenant_config and need their own save flow.

function OverviewTab({ tenant, subTenants, onSaved }) {
  const [form, setForm] = useState(() => ({
    name: tenant.name || "",
    legal_name: tenant.legal_name || "",
  }));
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importBusy, setImportBusy] = useState(false);

  useEffect(() => {
    setForm({
      name: tenant.name || "",
      legal_name: tenant.legal_name || "",
    });
  }, [tenant]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const dirty =
    form.name !== (tenant.name || "") ||
    form.legal_name !== (tenant.legal_name || "");

  const save = async () => {
    if (!form.name.trim()) {
      setSaveError(new Error("Name is required."));
      return;
    }
    setBusy(true);
    setSaveError(null);
    try {
      await api.updateTenant(tenant._id, {
        name: form.name.trim(),
        legal_name: form.legal_name.trim() || form.name.trim(),
      });
      if (onSaved) await onSaved();
    } catch (err) {
      setSaveError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="stat-grid">
        <StatCard label="Tenant ID" value={tenant._id} />
        <StatCard label="Sub-Tenants" value={subTenants.length} />
        <StatCard label="Created" value={formatDate(tenant.created_at)} />
      </div>

      <ErrorBanner error={saveError} />

      <div className="card">
        <div
          className="card-header"
          style={{ justifyContent: "space-between" }}
        >
          <h3>Identity</h3>
          <button
            className="btn primary sm"
            onClick={save}
            disabled={!dirty || busy}
          >
            {busy ? "Saving..." : "Save changes"}
          </button>
        </div>
        <div className="form-field">
          <label>Display name</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => update({ name: e.target.value })}
          />
        </div>
        <div className="form-field">
          <label>Legal name</label>
          <input
            type="text"
            placeholder="defaults to display name"
            value={form.legal_name}
            onChange={(e) => update({ legal_name: e.target.value })}
          />
        </div>
        <p className="muted small">
          LLM model, voice, capabilities, and brand voice live on the{" "}
          <strong>Config</strong> tab — they're versioned separately so
          you can roll them back independent of identity changes.
        </p>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>System metadata</h3>
        </div>
        <table className="table" style={{ border: "none" }}>
          <tbody>
            <tr>
              <th style={{ width: "30%" }}>Billing id</th>
              <td className="mono">
                {tenant.billing_id || <span className="subtle">—</span>}
              </td>
            </tr>
            <tr>
              <th>Main orchestrator template</th>
              <td className="mono">
                {tenant.main_orchestrator?.library_key || "tenant-orchestrator"}
              </td>
            </tr>
            <tr>
              <th>Cost lock</th>
              <td>
                {tenant.cost_locked_until ? (
                  <span className="badge badge-danger">
                    Locked until {formatDate(tenant.cost_locked_until)}
                  </span>
                ) : (
                  <span className="badge badge-live">Not locked</span>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Export / Import */}
      <div className="card">
        <div className="card-header">
          <h3>Config as Code</h3>
        </div>
        <p className="muted small mb-16">
          Export this tenant's entire configuration as a ZIP file.
          Edit the prompts and configs with Claude Code (a CLAUDE.md
          guide is included), then import the modified ZIP to update
          the tenant's draft.
        </p>
        <div className="btn-row" style={{ flexWrap: "wrap", gap: 12 }}>
          <a
            href={api.exportTenantUrl(tenant._id)}
            className="btn primary sm"
            download
          >
            Export ZIP
          </a>
          <label
            className="btn sm"
            style={{ cursor: "pointer" }}
          >
            Import ZIP
            <input
              type="file"
              accept=".zip"
              style={{ display: "none" }}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setImportBusy(true);
                setImportResult(null);
                try {
                  const res = await api.importTenantValidate(tenant._id, file);
                  setImportResult(res.data || res);
                } catch (err) {
                  setImportResult({
                    validated: false,
                    errors: [{ path: "(upload)", error: err.message }],
                  });
                } finally {
                  setImportBusy(false);
                  e.target.value = "";
                }
              }}
            />
          </label>
          {importBusy && (
            <span className="muted small">Validating...</span>
          )}
        </div>

        {importResult && (
          <div style={{ marginTop: 16 }}>
            {importResult.errors?.length > 0 && (
              <div className="error-banner" style={{ marginBottom: 12 }}>
                {importResult.errors.map((e, i) => (
                  <div key={i}>
                    <strong>{e.path}:</strong> {e.error}
                  </div>
                ))}
              </div>
            )}

            {importResult.validated !== false && (
              <div
                className="card"
                style={{
                  background: "#f0fff4",
                  borderColor: "#a3e4a5",
                }}
              >
                <p className="muted small" style={{ margin: "0 0 8px" }}>
                  <strong>
                    {importResult.modified || 0} modified
                  </strong>{" "}
                  · {importResult.unchanged || 0} unchanged ·{" "}
                  {importResult.total_files || 0} files scanned
                </p>

                {(importResult.modified || 0) > 0 && (
                  <>
                    <div style={{ marginBottom: 12 }}>
                      {(importResult.changes || [])
                        .filter((c) => c.action === "modified")
                        .map((c, i) => (
                          <div
                            key={i}
                            className="mono small"
                            style={{ padding: "2px 0" }}
                          >
                            {c.type}: {c.sub_tenant_id}/{c.agent_key || "config"}
                          </div>
                        ))}
                    </div>
                    <button
                      className="btn primary sm"
                      disabled={importBusy}
                      onClick={async () => {
                        setImportBusy(true);
                        try {
                          const res = await api.importTenantApply(
                            tenant._id,
                            importResult.changes.filter(
                              (c) => c.action === "modified"
                            )
                          );
                          setImportResult({
                            ...importResult,
                            applied: res.data,
                            done: true,
                          });
                        } catch (err) {
                          setSaveError(err);
                        } finally {
                          setImportBusy(false);
                        }
                      }}
                    >
                      {importBusy
                        ? "Applying..."
                        : `Apply ${importResult.modified} changes to draft`}
                    </button>
                  </>
                )}

                {importResult.done && importResult.applied && (
                  <p
                    className="muted small"
                    style={{ marginTop: 8 }}
                  >
                    Applied {importResult.applied.applied?.length || 0}{" "}
                    changes to draft.{" "}
                    {importResult.applied.errors?.length > 0 &&
                      `${importResult.applied.errors.length} errors.`}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Sub-tenants tab ─────────────────────────────────────────────────

function SubTenantsTab({ tid, subTenants: initialSubTenants }) {
  const [subTenants, setSubTenants] = useState(initialSubTenants);
  const [showAdd, setShowAdd] = useState(false);
  const [groups, setGroups] = useState([]);
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState(null);
  const nav = useNavigate();

  const openAdd = async () => {
    try {
      const res = await api.listSpecialtyCatalog();
      setGroups(res.data || []);
    } catch {}
    setShowAdd(true);
    setAddError(null);
  };

  const addSubTenant = async (group) => {
    const groupId = group.id || group._id;
    const subId = `${tid}-${groupId}`;
    setAddBusy(true);
    setAddError(null);
    try {
      await api.createSubTenant(tid, {
        id: subId,
        name: groupId,
        specialty: groupId,
        fork_from_specialty: groupId,
      });
      // Reload sub-tenants
      const res = await api.listSubTenants(tid);
      setSubTenants(res.data || []);
      setShowAdd(false);
    } catch (err) {
      setAddError(err.message || String(err));
    } finally {
      setAddBusy(false);
    }
  };

  return (
    <>
    {/* Add Sub-Tenant Modal */}
    {showAdd && (
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowAdd(false)}>
        <div style={{ background: "#fff", borderRadius: 8, padding: 24, minWidth: 450, maxWidth: 600, maxHeight: "70vh", overflow: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }} onClick={(e) => e.stopPropagation()}>
          <h3 style={{ marginBottom: 12 }}>Add Sub-Tenant from Library</h3>
          <p className="muted small mb-16">
            Select an agent group to create a new sub-tenant. Agents and orchestration will be forked from the library.
          </p>
          {addError && <ErrorBanner error={{ message: addError }} />}
          {(() => {
            const existingSpecialties = new Set(subTenants.map((s) => s.specialty));
            const available = groups.filter((g) => !existingSpecialties.has(g.id || g._id));
            if (available.length === 0) {
              return <p className="muted">All available agent groups are already added.</p>;
            }
            return available.map((g) => {
              const gid = g.id || g._id;
              return (
                <div key={gid} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--color-border)" }}>
                  <div>
                    <strong>{g.name}</strong>
                    <div className="muted small mono">{gid}</div>
                    {g.description && <div className="muted small" style={{ maxWidth: 350 }}>{g.description.slice(0, 80)}</div>}
                  </div>
                  <button className="btn btn-sm primary" disabled={addBusy} onClick={() => addSubTenant(g)}>
                    {addBusy ? "Creating..." : "Add"}
                  </button>
                </div>
              );
            });
          })()}
          <div style={{ marginTop: 16, textAlign: "right" }}>
            <button className="btn btn-sm" onClick={() => setShowAdd(false)}>Close</button>
          </div>
        </div>
      </div>
    )}

    {subTenants.length === 0 ? (
      <div className="empty-state">
        <h3>No sub-tenants</h3>
        <p className="muted">Add a sub-tenant by selecting an agent group from the library.</p>
        <button className="btn primary" style={{ marginTop: 12 }} onClick={openAdd}>
          + Add Sub-Tenant
        </button>
      </div>
    ) : (<>
    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
      <button className="btn btn-sm primary" onClick={openAdd}>
        + Add Sub-Tenant
      </button>
    </div>
    <div className="card" style={{ padding: 0 }}>
      <table className="table" style={{ border: "none" }}>
        <thead>
          <tr>
            <th>Name</th>
            <th>ID</th>
            <th>Specialty</th>
            <th>Enabled Agents</th>
            <th>Environment</th>
          </tr>
        </thead>
        <tbody>
          {subTenants.map((s) => (
            <tr key={s._id}>
              <td>
                <strong>{s.name}</strong>
              </td>
              <td className="mono">{s._id}</td>
              <td>{s.specialty}</td>
              <td>
                {s.enabled_agents == null ? (
                  <span className="badge badge-neutral">All</span>
                ) : (
                  <span className="badge badge-accent">{s.enabled_agents.length} of all</span>
                )}
              </td>
              <td>
                <div className="btn-row">
                  <Link
                    to={`/tenants/${tid}/sub-tenants/${s._id}/draft`}
                    className="btn sm"
                    style={{
                      background: "#2962ff",
                      color: "#fff",
                      textDecoration: "none",
                      padding: "4px 10px",
                      fontSize: 12,
                      borderRadius: 4,
                    }}
                  >
                    Draft
                  </Link>
                  <Link
                    to={`/tenants/${tid}/sub-tenants/${s._id}/live`}
                    className="btn sm"
                    style={{
                      background: "#5cb85c",
                      color: "#fff",
                      textDecoration: "none",
                      padding: "4px 10px",
                      fontSize: 12,
                      borderRadius: 4,
                    }}
                  >
                    Live
                  </Link>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </>)}
    </>
  );
}

// ─── Composed Prompt tab ─────────────────────────────────────────────

function ComposedTab({ tid }) {
  const [mode, setMode] = useState("live");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getComposedPrompt(tid, { mode, trackStaleness: true });
      setData(res);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [tid, mode]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Loading />;
  if (error) return <ErrorBanner error={error} />;

  return (
    <>
      <div className="mb-16" style={{ display: "flex", gap: 16, alignItems: "center" }}>
        <ModeToggle mode={mode} onChange={setMode} />
        <div className="spacer" />
        <span className="muted small">
          {data.metadata.char_count.toLocaleString()} chars · {data.metadata.sub_tenant_count} sub-tenant
          {data.metadata.sub_tenant_count === 1 ? "" : "s"} ·{" "}
          {data.metadata.is_proxy_mode ? "proxy mode" : "classifier mode"}
        </span>
      </div>

      {data.staleness_report &&
        (data.staleness_report.sub_tenant_prompts?.length > 0 ||
          data.staleness_report.overrides?.length > 0) && (
          <div className="error-banner" style={{ background: "#fff8c5", borderColor: "#9a6700", color: "#9a6700" }}>
            <strong>Stale content detected.</strong>{" "}
            {data.staleness_report.sub_tenant_prompts?.length || 0} forked prompts and{" "}
            {data.staleness_report.overrides?.length || 0} overrides are behind their master.
          </div>
        )}

      <div className="card">
        <div className="card-header">
          <h3>Routing / orchestration prompt ({mode})</h3>
        </div>
        <p className="muted small mb-16">
          This is the tenant-level routing prompt — it tells the agent how
          to dispatch incoming calls to the right sub-tenant. Per
          sub-tenant LLM config, capabilities, and prompts live on the
          sub-tenant detail page.
        </p>
        <PromptBody content={data.data} />
      </div>
    </>
  );
}

